"use client";

import { useEffect, useRef, useState } from "react";
import type { Column } from "@tanstack/react-table";
import { useDebouncedValue } from "@/hooks/useDebouncedValue";
import type { NumberRangeValue } from "../../NumberRangeInput";
import type { DateRangeValue } from "../../DateRangeInput";
import type { ColumnFilterConfig } from "../types";

const FILTER_DEBOUNCE_MS = 400;

export type FilterDraft = string | NumberRangeValue | DateRangeValue | string[];

function isCheckboxValue(value: unknown): value is string[] {
  return Array.isArray(value);
}

function isNumberRangeValue(value: unknown): value is NumberRangeValue {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

function isDateRangeValue(value: unknown): value is DateRangeValue {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

function isFilterActive(
  type: ColumnFilterConfig["type"],
  value: unknown,
): boolean {
  if (type === "numberRange") {
    return (
      isNumberRangeValue(value) &&
      (value.min !== undefined || value.max !== undefined)
    );
  }
  if (type === "dateRange") {
    return (
      isDateRangeValue(value) &&
      (value.from !== undefined || value.to !== undefined)
    );
  }
  if (type === "checkbox") {
    return isCheckboxValue(value) && value.length > 0;
  }
  return typeof value === "string" && value.trim() !== "";
}

function draftFromValue(
  type: ColumnFilterConfig["type"] | undefined,
  value: unknown,
): FilterDraft {
  if (type === "numberRange") {
    return isNumberRangeValue(value) ? value : {};
  }
  if (type === "dateRange") {
    return isDateRangeValue(value) ? value : {};
  }
  if (type === "checkbox") {
    return isCheckboxValue(value) ? value : [];
  }
  return typeof value === "string" ? value : "";
}

// Structural equality for the shapes a filter value can take, so that
// re-committing the same draft (a new object with identical fields) isn't
// mistaken for an external change and doesn't retrigger the resync effect.
function areFilterValuesEqual(a: unknown, b: unknown): boolean {
  if (a === b) return true;
  if (Array.isArray(a) && Array.isArray(b)) {
    return a.length === b.length && a.every((v, i) => v === b[i]);
  }
  if (
    typeof a === "object" &&
    a !== null &&
    typeof b === "object" &&
    b !== null &&
    !Array.isArray(a) &&
    !Array.isArray(b)
  ) {
    const aRecord = a as Record<string, unknown>;
    const bRecord = b as Record<string, unknown>;
    const keys = new Set([...Object.keys(aRecord), ...Object.keys(bRecord)]);
    return [...keys].every((key) => aRecord[key] === bRecord[key]);
  }
  return false;
}

/** Draft state for a column filter popover: buffers edits and applies them to the column filter after a debounce, across all filter shapes (text/numberRange/dateRange/checkbox). */
export function useColumnFilterDraft<TData, TValue>(
  column: Column<TData, TValue>,
  filter: ColumnFilterConfig | undefined,
) {
  const appliedValue = column.getFilterValue();
  const isActive = filter ? isFilterActive(filter.type, appliedValue) : false;

  const [draft, setDraft] = useState<FilterDraft>(() =>
    draftFromValue(filter?.type, appliedValue),
  );
  const debouncedDraft = useDebouncedValue(draft, FILTER_DEBOUNCE_MS);
  const isFirstRun = useRef(true);
  const lastAppliedValue = useRef(appliedValue);

  // Keeps the draft in sync when the filter is changed from outside this
  // popover — e.g. a "Clear all filters" action that calls
  // onColumnFiltersChange directly. Without this, the draft keeps showing
  // a value the column filter no longer has.
  useEffect(() => {
    if (areFilterValuesEqual(appliedValue, lastAppliedValue.current)) return;
    lastAppliedValue.current = appliedValue;
    setDraft(draftFromValue(filter?.type, appliedValue));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [appliedValue]);

  useEffect(() => {
    if (isFirstRun.current) {
      isFirstRun.current = false;
      return;
    }
    if (filter?.type === "checkbox" && isCheckboxValue(debouncedDraft)) {
      column.setFilterValue(
        debouncedDraft.length > 0 ? debouncedDraft : undefined,
      );
    } else if (
      filter?.type === "numberRange" &&
      isNumberRangeValue(debouncedDraft)
    ) {
      const { min, max } = debouncedDraft;
      column.setFilterValue(
        min === undefined && max === undefined ? undefined : { min, max },
      );
    } else if (
      filter?.type === "dateRange" &&
      isDateRangeValue(debouncedDraft)
    ) {
      const { from, to } = debouncedDraft;
      column.setFilterValue(
        from === undefined && to === undefined ? undefined : { from, to },
      );
    } else if (typeof debouncedDraft === "string") {
      column.setFilterValue(debouncedDraft || undefined);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedDraft]);

  const clear = () => {
    if (filter?.type === "numberRange" || filter?.type === "dateRange")
      setDraft({});
    else if (filter?.type === "checkbox") setDraft([]);
    else setDraft("");
    column.setFilterValue(undefined);
  };

  return { draft, setDraft, isActive, clear };
}
